import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { useParams, useLocation, Link } from 'react-router-dom';
import { useHistory } from 'react-router';
import {
  DeprecatedButtonGroup as ButtonGroup,
  BodyTextMedium,
  Button,
  renderMessage,
  HeadingText40,
  Checkbox,
  BodyTextSmall,
  DeprecatedInlineMessage as InlineMessage,
} from '@modernhealth/vital-components';
import { captureException as sentryCaptureException } from '@sentry/react';
import { AnalyticsEventType, useAnalytics } from '@modernhealth/web-analytics';

import {
  CircleRsvpQuestionAnswerInput,
  CircleRsvpUnauthenticatedUserInfoInput,
} from 'src/generated/graphql';
import useShowSkeletonDelay from 'src/shared/hooks/useShowSkeletonDelay';
import { filterNullish } from 'src/shared/typeUtils/filterNullish';
import { currentTimezone } from 'src/shared/utils/timezone';
import { BackButton } from 'src/shared/components/BackButton';
import * as Constants from 'src/shared/data/constants';
import { captureException } from 'src/shared/utils/captureException';

import { SeriesAccessType } from '../utils/formatSeriesAccessType';
import Questions from '../components/RsvpQuestions';
import UserInfo from '../components/RsvpUserInfo';
import { TopicHeaderCard } from '../components';
import { useFetchTopicQuery } from '../documents/topic.generated';
import { useCreateUnauthenticatedRsvpMutation } from '../documents/rsvp.generated';
import TopicPageSkeleton from './skeletons/TopicPageSkeleton';
import { QuestionAnswer, QuestionAnswers } from '../types';
import { isEmailValid } from '../components/utils/emailValidation';

interface RsvpProps {
  path?: string;
  sessionId: string;
  topicSlug: string;
}

type ErrorType =
  | 'CircleRsvpSuccessful'
  | 'CircleNotFoundError'
  | 'CircleValidationError'
  | 'CircleProcessingError'
  | 'CircleSessionSeatLimitExceededError'
  | 'CircleSeriesAccessTypeNotAllowedError'
  | 'CircleSessionAlreadyStartedError'
  | `Circle${string}Error`;

const PageWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  maxWidth: '32rem',
  margin: '0 auto',
});

const PageContent = styled.div({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: 16,
});

const RsvpPage: React.FC<unknown> = () => {
  const analytics = useAnalytics();
  const history = useHistory();
  const location = useLocation();
  const { sessionId } = useParams<RsvpProps>();

  const [formValues, setFormValues] = React.useState<
    Partial<CircleRsvpUnauthenticatedUserInfoInput>
  >({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    isMember: undefined,
  });

  const [questionAnswers, setQuestionAnswers] = React.useState<QuestionAnswers>({});
  const [showErrors, setShowErrors] = React.useState(false);
  const [termsAgreed, setTermsAgreed] = React.useState(false);
  const [privacyAgreed, setPrivacyAgreed] = React.useState(false);
  const [communityGuidelinesAgreed, setCommunityGuidelinesAgreed] = React.useState(false);
  const [overEighteen, setOverEighteen] = React.useState(false);
  const [isRsvpButtonDisabled, setIsRsvpButtonDisabled] = React.useState(false);

  const [createRsvp] = useCreateUnauthenticatedRsvpMutation();
  const { topicSlug } = useParams<RsvpProps>();
  const { data, loading, refetch } = useFetchTopicQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      topicSlug: topicSlug,
    },
  });
  const isMembersOnly =
    data?.circleTopicBySlug?.series.accessType === SeriesAccessType.MODERN_HEALTH_MEMBERS_ONLY;

  const questions = useMemo(() => {
    return filterNullish(data?.circleTopicBySlug?.rsvpQuestions ?? []);
  }, [data?.circleTopicBySlug?.rsvpQuestions]);

  const setQuestionAnswer = ({ id, answerText, answerId }: QuestionAnswer) => {
    setQuestionAnswers({ ...questionAnswers, [id]: { answerId, answerText } });
  };

  const setFormValue = (field: string, value?: string | boolean) => {
    setFormValues({ ...formValues, [field]: value });
  };

  const isMemberValid = () => {
    // Membership validation is only required if its a members-only circle
    return !isMembersOnly;
  };

  const isCompanyValid = (company?: string | null, isMember?: boolean) => {
    // Company/Organization field is only required if user is a member
    if (isMember && !company) return false;
    return true;
  };

  const isQuestionaireValid = () => {
    // setting blank answers for required questions
    const requiredQuestions = questions?.filter(question => question.required);
    const requiredQuestionIds = requiredQuestions.map(question => question.id);

    const allRequiredAnswersArePresent = requiredQuestionIds.every(
      requiredQuestionId =>
        requiredQuestionId in questionAnswers && !!questionAnswers[requiredQuestionId],
    );

    return allRequiredAnswersArePresent;
  };

  const isFormValid = (
    values: Partial<CircleRsvpUnauthenticatedUserInfoInput>,
  ): values is Required<Partial<CircleRsvpUnauthenticatedUserInfoInput>> => {
    const { firstName, lastName, email, isMember, country, company } = values;
    // if any required questions are missing answers set showErrors to true
    const questionsDone = isQuestionaireValid();

    if (!questionsDone) return false;
    return !!(
      firstName &&
      lastName &&
      country &&
      isEmailValid(email) &&
      isMemberValid() &&
      isCompanyValid(company, isMember) &&
      termsAgreed &&
      privacyAgreed &&
      communityGuidelinesAgreed &&
      overEighteen
    );
  };

  const formatAnswers = (answers: QuestionAnswers): CircleRsvpQuestionAnswerInput[] => {
    const answersEntries = Object.entries(answers);
    const formattedAnswers = answersEntries.map(([questionId, { answerId, answerText }]) => ({
      questionId,
      answerId,
      answerText: answerText?.toString(),
    }));
    return formattedAnswers;
  };

  const addRsvp = async () => {
    const rsvpQuestionAnswers = formatAnswers(questionAnswers);

    const { data: circleCreateUnauthenticatedRsvp } = await createRsvp({
      variables: {
        data: {
          sessionId,
          userInfo: {
            firstName: formValues.firstName ?? '',
            lastName: formValues.lastName ?? '',
            email: formValues.email ?? '',
            company: formValues.company,
            country: formValues.country ?? '',
            isMember: formValues.isMember ?? false,
            timezone: currentTimezone(),
          },
          rsvpQuestionAnswers,
        },
      },
    });
    return circleCreateUnauthenticatedRsvp;
  };

  //TODO: RSVP Skeleton- Using TopicPageSkeleton for now
  const show = useShowSkeletonDelay(!!data);

  if (show || (loading && !data)) {
    return <TopicPageSkeleton />;
  }

  const getErrorMessage = (type?: ErrorType | undefined): string => {
    const defaultErrorMessage =
      'An error occurred. If this persists, please reach out to help@modernhealth.com';
    if (!type) return defaultErrorMessage;
    const messagesDict: Record<string, string> = {
      CircleSessionAlreadyStartedError: 'Session already started, please select another session',
      CircleSessionSeatLimitExceededError: 'Session fully booked, please select another session',
    };
    return messagesDict[type] ?? defaultErrorMessage;
  };

  const numberOfCustomQuestions = data?.circleTopicBySlug?.rsvpQuestions.length;
  const memberRegistrationUrl =
    numberOfCustomQuestions && numberOfCustomQuestions > 0
      ? `${process.env.REACT_APP_MEMBER_WEB_URL}/circles/${topicSlug}/session/${sessionId}/register`
      : `${process.env.REACT_APP_MEMBER_WEB_URL}/circles/${topicSlug}`;

  return data ? (
    <PageWrapper>
      <BackButton />
      <PageContent>
        {data.circleTopicBySlug && (
          <TopicHeaderCard topic={data?.circleTopicBySlug} sessionId={sessionId} />
        )}
        {isMembersOnly ? (
          <>
            <InlineMessage
              heading="Members Only"
              message="We're sorry, this Circle is reserved for members only."
            >
              <Link
                to={memberRegistrationUrl}
                css={{ textDecoration: 'underline', marginBottom: 8, marginTop: 8 }}
              >
                Register as a member
              </Link>
              <Link to="/" css={{ textDecoration: 'underline' }}>
                Explore upcoming circles
              </Link>
            </InlineMessage>
            <Button
              onClick={() => {
                history.goBack();
              }}
              title="Back"
              size="large"
              variant="secondary"
            />
          </>
        ) : (
          <>
            <UserInfo formValues={formValues} setFormValue={setFormValue} showErrors={showErrors} />

            <Questions
              questions={questions}
              questionAnswers={questionAnswers}
              setQuestionAnswer={setQuestionAnswer}
              showErrors={showErrors}
            />
            {/* Terms and conditions section */}
            <div css={{ marginBottom: '40px' }}>
              <HeadingText40 css={{ marginBottom: '16px' }}>Terms and Conditions</HeadingText40>
              <BodyTextMedium css={{ marginBottom: '16px' }}>
                By registering for this session, you agree to Modern Health’s
                <a target="_blank" href={Constants.TERMS_OF_USE_URL} rel="noreferrer">
                  {' '}
                  Terms of Use
                </a>
                {', '}
                <a
                  target="_blank"
                  href={Constants.CIRCLES_COMMUNITY_GUIDELINES_URL}
                  rel="noreferrer"
                >
                  {' '}
                  Circles Community Guidelines
                </a>
                {', '}
                and
                <a target="_blank" href={Constants.PRIVACY_URL} rel="noreferrer">
                  {' '}
                  Privacy Policy
                </a>
                .
              </BodyTextMedium>
              <div css={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* temporary checkbox wrapper until we enable passing css in Vita*/}
                <div>
                  <Checkbox
                    value={termsAgreed ?? false}
                    label="I agree to Modern Health's Terms of Use."
                    onChange={(value: boolean) => {
                      setTermsAgreed(value);
                    }}
                  />
                  {showErrors && !termsAgreed && (
                    <BodyTextSmall
                      css={{ paddingLeft: '8px', paddingTop: '8px' }}
                      fontColor="red55"
                    >
                      You must agree to the Terms of Use to register.
                    </BodyTextSmall>
                  )}
                </div>
                <div>
                  <Checkbox
                    value={communityGuidelinesAgreed ?? false}
                    label="I agree to the Circles Community Guidelines."
                    onChange={(value: boolean) => {
                      setCommunityGuidelinesAgreed(value);
                    }}
                  />
                  {showErrors && !communityGuidelinesAgreed && (
                    <BodyTextSmall
                      css={{ paddingLeft: '8px', paddingTop: '8px' }}
                      fontColor="red55"
                    >
                      You must agree to the Circles Community Guidelines to register.
                    </BodyTextSmall>
                  )}
                </div>
                <div>
                  <Checkbox
                    value={privacyAgreed ?? false}
                    label="I consent to Modern Health's Privacy Notice."
                    onChange={(value: boolean) => {
                      setPrivacyAgreed(value);
                    }}
                  />
                  {showErrors && !privacyAgreed && (
                    <BodyTextSmall
                      css={{ paddingLeft: '8px', paddingTop: '8px' }}
                      fontColor="red55"
                    >
                      You must consent to the Privacy Notice to register.
                    </BodyTextSmall>
                  )}
                </div>
                <div>
                  <Checkbox
                    value={overEighteen ?? false}
                    label="I am at least eighteen (18) years of age."
                    onChange={(value: boolean) => {
                      setOverEighteen(value);
                    }}
                  />
                  {showErrors && !overEighteen && (
                    <BodyTextSmall
                      css={{ paddingLeft: '8px', paddingTop: '8px' }}
                      fontColor="red55"
                    >
                      You must be at least eighteen (18) years of age to register.
                    </BodyTextSmall>
                  )}
                </div>
              </div>
            </div>
            <ButtonGroup
              firstButtonText="Back"
              onFirstButtonClick={() => {
                history.goBack();
              }}
              secondButtonText="Confirm"
              secondButtonDisabled={isMembersOnly || isRsvpButtonDisabled}
              onSecondButtonClick={() => {
                analytics.trackClick('Circles: Submit RSVP Form');

                setIsRsvpButtonDisabled(true);
                if (!isFormValid(formValues)) {
                  setShowErrors(true);
                  setIsRsvpButtonDisabled(false);

                  return;
                }
                addRsvp()
                  .then(res => {
                    const rsvp = res?.circleCreateUnauthenticatedRsvp;

                    if (rsvp && rsvp.__typename === 'CircleRsvpSuccessful') {
                      const localLocation = {
                        pathname: `${location.pathname}/confirmation/${rsvp.rsvpId}`,
                        state: { email: formValues.email },
                      };
                      history.push(localLocation);
                    } else {
                      renderMessage({
                        content: getErrorMessage(rsvp?.__typename),
                        type: 'error',
                        duration: 5,
                      });
                      setIsRsvpButtonDisabled(false);
                    }
                  })
                  .catch(error => {
                    sentryCaptureException(error);
                    captureException(error);
                    renderMessage({
                      content: getErrorMessage(),
                      type: 'error',
                      duration: 5,
                    });
                    setIsRsvpButtonDisabled(false);
                  });
              }}
            />
          </>
        )}
      </PageContent>
    </PageWrapper>
  ) : (
    <BodyTextMedium>
      Couldn't load,{' '}
      <Button
        title="retry?"
        onClick={() => {
          refetch();

          analytics.track('Circles: Registration Form Failed to Load', AnalyticsEventType.ERROR);
          analytics.trackClick('Circles: Reload Registration Form');
        }}
      >
        retry?
      </Button>
    </BodyTextMedium>
  );
};

export default RsvpPage;
